import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLottery } from "@/hooks/use-lottery-selection";
import { PlanGate } from "@/components/app/PlanGate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Sigma, Scale, RotateCw, Network } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  Line,
  LineChart,
  ReferenceLine,
} from "recharts";

type Period = "20" | "100" | "500" | "all";
type SeriesPeriod = "20" | "50" | "100";

type AdvancedStats = {
  lottery_slug: string;
  numbers_drawn: number;
  universe_max: number;
  total_draws: number;
  sum_distribution: { min: number; max: number; count: number }[];
  parity_distribution: { evens: number; odds: number; count: number }[];
  repeat_distribution: { repeated: number; count: number }[];
  cycles: { number: number; avg_cycle: number; max_cycle: number; appearances: number }[];
  top_pairs: { n1: number; n2: number; count: number; last_contest?: number | null; delay?: number | null }[];
  max_contest?: number | null;
};

const TOOLTIP_STYLE = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--lt-border))",
  borderRadius: "8px",
  fontSize: "12px",
  color: "hsl(var(--foreground))",
};
const TOOLTIP_ITEM_STYLE = { color: "hsl(var(--foreground))" };
const TOOLTIP_LABEL_STYLE = { color: "hsl(var(--foreground))", fontWeight: 500 };
const TOOLTIP_CURSOR = { fill: "hsl(var(--foreground) / 0.08)" };

const EstatisticasContent = () => {
  const { selected, config } = useLottery();
  const [period, setPeriod] = useState<Period>("100");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["advanced-stats", selected, period],
    queryFn: async () => {
      const lastN = period === "all" ? null : Number(period);
      const { data, error } = await supabase.rpc("compute_advanced_stats", {
        _lottery_slug: selected,
        _last_n: lastN,
      });
      if (error) throw error;
      return data as unknown as AdvancedStats;
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-lt-border bg-lt-bg2 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            Estatísticas avançadas
          </div>
          <h1 className="font-serif text-3xl font-light text-foreground">
            Análise <em className="not-italic text-primary">profunda</em> — {config.name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Soma das dezenas, paridade histórica, repetições entre concursos, ciclos de retorno e
            os pares de números que mais saem juntos. Tudo calculado sobre{" "}
            {data?.total_draws ? `${data.total_draws.toLocaleString("pt-BR")} concursos` : "o histórico"}.
          </p>
        </div>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <TabsList className="bg-card border border-lt-border">
            <TabsTrigger value="20">Últimos 20</TabsTrigger>
            <TabsTrigger value="100">Últimos 100</TabsTrigger>
            <TabsTrigger value="500">Últimos 500</TabsTrigger>
            <TabsTrigger value="all">Histórico completo</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isError ? (
        <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
          Não foi possível calcular as estatísticas deste período agora.
          <div className="mt-3 flex justify-center gap-2">
            <button className="rounded-md border border-border px-3 py-1.5 text-foreground" onClick={() => refetch()}>Tentar novamente</button>
            {period !== "100" && <button className="rounded-md border border-border px-3 py-1.5 text-foreground" onClick={() => setPeriod("100")}>Ver últimos 100</button>}
          </div>
        </div>
      ) : isLoading || !data ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-72 w-full bg-lt-bg2" />
          ))}
        </div>
      ) : !data.total_draws || data.sum_distribution.length === 0 ? (
        <Card className="border-lt-border bg-lt-bg2">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Ainda não temos histórico suficiente de <strong>{config.name}</strong> para calcular
            estas estatísticas. Tente novamente em instantes.
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="sum" className="space-y-6">
          <TabsList className="bg-card border border-lt-border">
            <TabsTrigger value="sum" className="gap-2">
              <Sigma className="h-3.5 w-3.5" /> Soma
            </TabsTrigger>
            <TabsTrigger value="parity" className="gap-2">
              <Scale className="h-3.5 w-3.5" /> Paridade
            </TabsTrigger>
            <TabsTrigger value="cycles" className="gap-2">
              <RotateCw className="h-3.5 w-3.5" /> Ciclos
            </TabsTrigger>
            <TabsTrigger value="pairs" className="gap-2">
              <Network className="h-3.5 w-3.5" /> Pares
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sum">
            <SumSection data={data} />
          </TabsContent>
          <TabsContent value="parity">
            <ParitySection data={data} />
          </TabsContent>
          <TabsContent value="cycles">
            <CyclesSection data={data} />
          </TabsContent>
          <TabsContent value="pairs">
            <PairsSection data={data} />
          </TabsContent>
        </Tabs>
      )}

      {!isLoading && data && data.total_draws > 0 && (
        <BallSeriesSection lotterySlug={selected} />
      )}
    </div>
  );
};

const SumSection = ({ data }: { data: AdvancedStats }) => {
  const chartData = data.sum_distribution.map((b) => ({
    range: `${b.min}–${b.max}`,
    count: b.count,
    label: `${b.min}–${b.max}`,
  }));
  const total = data.sum_distribution.reduce((a, b) => a + b.count, 0) || 1;
  const peak = data.sum_distribution.reduce(
    (m, b) => (b.count > m.count ? b : m),
    data.sum_distribution[0],
  );
  const min = data.sum_distribution[0];
  const max = data.sum_distribution[data.sum_distribution.length - 1];

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Distribuição da soma</CardTitle>
          <p className="text-xs text-muted-foreground">
            Em quantas vezes a soma das {data.numbers_drawn} dezenas caiu em cada faixa.
          </p>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid stroke="hsl(var(--lt-border))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="range"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <RTooltip
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                cursor={TOOLTIP_CURSOR}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill="hsl(var(--primary))" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Pontos-chave</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Stat
            label="Faixa mais comum"
            value={`${peak.min}–${peak.max}`}
            sub={`${peak.count} concursos · ${((peak.count / total) * 100).toFixed(1)}%`}
          />
          <Stat label="Soma mínima registrada" value={String(min.min)} sub={`${min.count} ocorrências`} />
          <Stat label="Soma máxima registrada" value={String(max.max)} sub={`${max.count} ocorrências`} />
          <p className="rounded-md border border-lt-border bg-card/50 p-3 text-[11px] leading-relaxed text-muted-foreground">
            Apostas com soma muito fora da faixa central são estatisticamente menos comuns —
            mas todas têm a mesma chance de sair em qualquer concurso.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const ParitySection = ({ data }: { data: AdvancedStats }) => {
  const chartData = data.parity_distribution.map((p) => ({
    label: `${p.evens}P / ${p.odds}I`,
    count: p.count,
  }));
  const total = data.parity_distribution.reduce((a, b) => a + b.count, 0) || 1;
  const top = [...data.parity_distribution].sort((a, b) => b.count - a.count)[0];

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Pares vs Ímpares</CardTitle>
          <p className="text-xs text-muted-foreground">
            Quantos concursos saíram com cada combinação de pares (P) e ímpares (I).
          </p>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid stroke="hsl(var(--lt-border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <RTooltip
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                cursor={TOOLTIP_CURSOR}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="hsl(var(--lt-teal))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Mais frequente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Stat
            label="Combinação mais comum"
            value={`${top.evens} pares / ${top.odds} ímpares`}
            sub={`${top.count} concursos · ${((top.count / total) * 100).toFixed(1)}%`}
          />
          <div className="space-y-1.5 text-[11px]">
            {data.parity_distribution.map((p) => {
              const pct = (p.count / total) * 100;
              return (
                <div key={p.evens} className="space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      {p.evens}P / {p.odds}I
                    </span>
                    <span className="tabular-nums text-foreground">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1 overflow-hidden rounded-full bg-card">
                    <div className="h-full bg-lt-teal" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="rounded-md border border-lt-border bg-card/50 p-3 text-[11px] leading-relaxed text-muted-foreground">
            Equilibrar pares e ímpares aproxima sua aposta da faixa estatisticamente dominante.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const CyclesSection = ({ data }: { data: AdvancedStats }) => {
  const repeatTotal = data.repeat_distribution.reduce((a, b) => a + b.count, 0) || 1;
  const sortedCycles = useMemo(
    () => [...data.cycles].sort((a, b) => Number(a.avg_cycle) - Number(b.avg_cycle)),
    [data.cycles],
  );
  const fastest = sortedCycles.slice(0, 5);
  const slowest = sortedCycles.slice(-5).reverse();

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Repetições do concurso anterior</CardTitle>
          <p className="text-xs text-muted-foreground">
            Quantos números do sorteio anterior costumam aparecer no próximo.
          </p>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          {data.repeat_distribution.map((r) => {
            const pct = (r.count / repeatTotal) * 100;
            return (
              <div key={r.repeated} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">
                    {r.repeated} {r.repeated === 1 ? "número repetido" : "números repetidos"}
                  </span>
                  <span className="tabular-nums text-foreground">
                    {r.count} · {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-card">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-xl font-light">Ciclo médio das dezenas</CardTitle>
          <p className="text-xs text-muted-foreground">
            Concursos médios entre uma aparição e a próxima.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="mb-2 text-[10px] uppercase tracking-wider text-lt-teal">Mais rápidas</h4>
              <ul className="space-y-1.5">
                {fastest.map((c) => (
                  <li key={c.number} className="flex items-center justify-between text-xs">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-lt-teal/40 bg-lt-teal/10 font-mono text-[11px] tabular-nums text-foreground">
                      {c.number.toString().padStart(2, "0")}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {Number(c.avg_cycle).toFixed(1)} concursos
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="mb-2 text-[10px] uppercase tracking-wider text-primary">Mais lentas</h4>
              <ul className="space-y-1.5">
                {slowest.map((c) => (
                  <li key={c.number} className="flex items-center justify-between text-xs">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-mono text-[11px] tabular-nums text-foreground">
                      {c.number.toString().padStart(2, "0")}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {Number(c.avg_cycle).toFixed(1)} concursos
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PairsSection = ({ data }: { data: AdvancedStats }) => {
  const top = data.top_pairs.slice(0, 20);
  const maxCount = top[0]?.count ?? 1;

  return (
    <Card className="border-lt-border bg-lt-bg2">
      <CardHeader>
        <CardTitle className="font-serif text-xl font-light">Top 20 pares mais frequentes</CardTitle>
        <p className="text-xs text-muted-foreground">
          Combinações de duas dezenas que mais apareceram no mesmo sorteio. O <strong>atraso</strong> mostra há
          quantos concursos a dupla não é sorteada junta e o <strong>último</strong> indica o concurso da última
          aparição conjunta.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {top.map((p, idx) => {
            const pct = (p.count / maxCount) * 100;
            const delay = p.delay ?? null;
            const last = p.last_contest ?? null;
            return (
              <div
                key={`${p.n1}-${p.n2}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-lt-border bg-card/50 px-3 py-2"
              >
                <span className="w-5 text-[10px] tabular-nums text-muted-foreground">{idx + 1}</span>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-mono text-[11px] tabular-nums text-foreground">
                    {p.n1.toString().padStart(2, "0")}
                  </span>
                  <span className="text-muted-foreground">+</span>
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-mono text-[11px] tabular-nums text-foreground">
                    {p.n2.toString().padStart(2, "0")}
                  </span>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-1.5 overflow-hidden rounded-full bg-card">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <span className="tabular-nums text-xs text-foreground" title="Concursos em que saíram juntas">
                  {p.count}×
                </span>
                <span
                  className="rounded-md border border-lt-border bg-background px-2 py-0.5 text-[10px] tabular-nums text-muted-foreground"
                  title="Concursos sem sair juntas"
                >
                  Atraso:{" "}
                  <span className={delay != null && delay > 0 ? "text-primary font-medium" : "text-foreground"}>
                    {delay != null ? delay : "—"}
                  </span>
                </span>
                <span
                  className="rounded-md border border-lt-border bg-background px-2 py-0.5 text-[10px] tabular-nums text-muted-foreground"
                  title="Último concurso em que saíram juntas"
                >
                  Último: <span className="text-foreground">{last != null ? `#${last}` : "—"}</span>
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-serif text-2xl font-light text-foreground">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

const BALL_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--lt-teal))",
  "hsl(var(--accent))",
  "hsl(var(--lt-purple))",
  "hsl(var(--lt-gold))",
];

const BallSeriesSection = ({
  lotterySlug,
}: {
  lotterySlug: string;
}) => {
  const [seriesPeriod, setSeriesPeriod] = useState<SeriesPeriod>("50");
  const { data, isLoading } = useQuery({
    queryKey: ["ball-series", lotterySlug, seriesPeriod],
    queryFn: async () => {
      const limit = Number(seriesPeriod);
      const { data: lottery, error: lerr } = await supabase
        .from("lotteries")
        .select("id, numbers_drawn")
        .eq("slug", lotterySlug)
        .single();
      if (lerr || !lottery) throw lerr ?? new Error("Loteria não encontrada");

      const { data: draws, error: derr } = await supabase
        .from("draws")
        .select("contest_number, draw_numbers(number)")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false })
        .limit(limit);
      if (derr) throw derr;

      const ordered = (draws ?? [])
        .map((d) => ({
          contest: d.contest_number,
          numbers: [...((d.draw_numbers ?? []) as { number: number }[])]
            .map((n) => n.number)
            .sort((a, b) => a - b),
        }))
        .reverse();

      return { ordered, picks: lottery.numbers_drawn as number };
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !data) {
    return (
      <Card className="border-lt-border bg-lt-bg2">
        <CardContent className="p-6">
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  const { ordered, picks } = data;
  if (ordered.length === 0) return null;

  const ballIndexes = Array.from({ length: picks }, (_, i) => i);

  return (
    <Card className="border-lt-border bg-lt-bg2">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="font-serif text-xl font-light">
              Sequência das bolas — últimos {ordered.length} concursos
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Para cada posição (Bola 1 = menor dezena sorteada … Bola {picks} = maior), o valor
              observado em cada concurso. As linhas pontilhadas marcam <strong>mínimo</strong>,{" "}
              <strong>média</strong> e <strong>máximo</strong> da posição no período.
            </p>
          </div>
          <Tabs value={seriesPeriod} onValueChange={(v) => setSeriesPeriod(v as SeriesPeriod)}>
            <TabsList className="bg-card border border-lt-border">
              <TabsTrigger value="20">Últimos 20</TabsTrigger>
              <TabsTrigger value="50">Últimos 50</TabsTrigger>
              <TabsTrigger value="100">Últimos 100</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6">
          {ballIndexes.map((i) => {
            const values = ordered.map((d) => d.numbers[i]).filter((n): n is number => n != null);
            if (values.length === 0) return null;
            const min = Math.min(...values);
            const max = Math.max(...values);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            const series = ordered.map((d) => ({
              contest: d.contest,
              value: d.numbers[i] ?? null,
            }));
            const color = BALL_COLORS[i % BALL_COLORS.length];
            return (
              <div
                key={i}
                className="rounded-lg border border-lt-border bg-card/50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-serif text-base text-foreground">
                    Bola {i + 1}
                  </span>
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    min {min} · méd {avg.toFixed(1)} · max {max}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={series} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--lt-border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="contest"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                      minTickGap={24}
                    />
                    <YAxis
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                      domain={[1, "dataMax"]}
                      width={40}
                    />
                    <RTooltip
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      cursor={TOOLTIP_CURSOR}
                      formatter={(v: number) => [String(v).padStart(2, "0"), `Bola ${i + 1}`]}
                      labelFormatter={(v) => `Concurso #${v}`}
                    />
                    <ReferenceLine y={min} stroke="hsl(var(--lt-teal))" strokeDasharray="2 3" strokeWidth={1} />
                    <ReferenceLine y={avg} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" strokeWidth={1} />
                    <ReferenceLine y={max} stroke="hsl(var(--primary))" strokeDasharray="2 3" strokeWidth={1} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={color}
                      strokeWidth={1.6}
                      dot={{ r: 1.5, fill: color }}
                      activeDot={{ r: 3 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const Estatisticas = () => (
  <PlanGate
    tier="pro"
    featureName="Estatísticas avançadas"
    description="Soma, paridade, ciclos e mapa de pares calculados sobre todo o histórico. Disponível no plano Pro."
  >
    <EstatisticasContent />
  </PlanGate>
);

export default Estatisticas;